import apiRequest, { setAuthHeader } from '../../api/request'
import { refresh } from '../../api/auth'
import { clearAuthData, getAuthToken, setAuthToken } from '../../storage/authStorage'

export function installAuthGuard(router) {
  let sessionValidation = null
  let backgroundValidation = null

  const validateSession = () => {
    if (!sessionValidation) {
      sessionValidation = verifyToken()
        .catch(() => refreshToken())
        .finally(() => {
          sessionValidation = null
        })
    }

    return sessionValidation
  }

  const redirectToLogin = (route) => {
    clearAuthData()

    const redirect = route?.fullPath || '/'
    return router.replace({
      path: '/login',
      query: { redirect }
    })
  }

  const validateSessionInBackground = (route) => {
    const validation = validateSession()
    if (backgroundValidation === validation) return

    backgroundValidation = validation
    validation
      .catch(() => {
        const currentRoute = router.currentRoute?.value
        if (currentRoute?.name === 'login') return

        return Promise.resolve(redirectToLogin(currentRoute || route)).catch(() => {})
      })
      .finally(() => {
        if (backgroundValidation === validation) {
          backgroundValidation = null
        }
      })
  }

  router.beforeEach(async (to, from, next) => {
    window.scrollTo(0, 0)

    if (to.name === 'alarmviewer' || to.meta?.public) {
      return next()
    }

    if (to.name === 'login') {
      if (getAuthToken()) {
        return next({ path: '/' })
      }
      return next()
    }

    if (!getAuthToken()) {
      clearAuthData()
      return next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
    }

    if (from?.name) {
      next()
      validateSessionInBackground(to)
      return
    }

    try {
      await validateSession()
      return next()
    } catch (error) {
      clearAuthData()
      return next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
    }
  })
}

async function verifyToken() {
  const token = getAuthToken()

  if (!token || token === '') {
    throw new Error('No token available')
  }

  setAuthHeader(token)

  try {
    await apiRequest({ url: '/verify' })
  } catch (error) {
    throw new Error('Token verification failed')
  }
}

async function refreshToken() {
  try {
    const response = await refresh()
    const newToken = response.data.token

    if (!newToken) {
      throw new Error('Token refresh did not return a token')
    }

    setAuthToken(newToken)
    setAuthHeader(newToken)

    await apiRequest({ url: '/verify' })
  } catch (error) {
    throw new Error('Token refresh failed')
  }
}
